import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'
import { Menu, X, Sun, Moon, LogOut, Search } from '@carouselgg/nexus/icon'
import { UserMenu } from '@carouselgg/nexus/user-menu'
import { useTheme } from '@carouselgg/nexus/theme'
import { LogoGradient } from '@carouselgg/nexus/brand'
import { useSearchOverlay, searchShortcutHint } from '@carouselgg/nexus/search-overlay'
import { useAuthStore } from '@carouselgg/auth/store'
import { useAccountMenu } from '@carouselgg/auth/account-menu'
import { AppIndexSearch } from '@carouselgg/auth/app-index-search'
import styles from './MarketingNav.module.css'

// `/dashboard` is a public route — it renders discovery cards and news for
// signed-out visitors too — so it belongs in the nav in both states.
//
// Two discovery doors, not three. Leagues, Organizations and Universities were
// three values of an internal `type` column, not three things a visitor wants:
// they want to enter a competition, or find people. The per-type pitch pages
// live at /for/* and are reached from the homepage and the footer.
const LINKS = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/compete', label: 'Compete' },
  { to: '/orgs', label: 'Organizations' },
  { to: '/news', label: 'News' },
]

/**
 * Nav for the public marketing surface.
 *
 * Deliberately not AppNav: that one is product chrome and pulls in
 * notifications and org switching, which a visitor who has not signed up has
 * no use for. Search is the exception — the Carousel Index is public (its
 * endpoints need no session), and finding a player, team or league is often
 * the first thing a visitor comes for.
 */
export function MarketingNav() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const { theme, toggleTheme } = useTheme()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const accountMenu = useAccountMenu()
  const search = useSearchOverlay()

  // A route change with the drawer open would otherwise leave it covering the
  // page the visitor just asked for.
  useEffect(() => {
    setOpen(false)
  }, [pathname])

  const links = LINKS.map((link) => (
    <NavLink key={link.to} to={link.to} className={styles.link}>
      {link.label}
    </NavLink>
  ))

  const themeToggle = (
    <button
      type="button"
      className={styles.iconButton}
      onClick={toggleTheme}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  )

  // Signed in: the one account menu (useAccountMenu), identical on every
  // surface, plus a Dashboard button as the way back into the product. Without
  // it the public surface was a dead end for a signed-in visitor. The theme
  // toggle stays in the bar at every width here, so the menu leaves its own
  // dark-mode switch out.
  const account = isAuthenticated && user ? (
    <>
      <UserMenu {...accountMenu} themeRow={false} />
      <Link to="/dashboard" className={styles.cta}>
        Dashboard
      </Link>
    </>
  ) : (
    <>
      <Link to="/login" className={styles.login}>
        Log in
      </Link>
      <Link to="/signup" className={styles.cta}>
        Sign up
      </Link>
    </>
  )

  return (
    <>
      <nav className={styles.nav}>
        <Link to="/" className={styles.brand}>
          <img src={LogoGradient} alt="" className={styles.logo} />
          <span className={styles.wordmark}>Carousel</span>
        </Link>

        <div className={styles.links}>{links}</div>

        <div className={styles.actions}>
          <button
            ref={search.triggerRef}
            type="button"
            className={styles.searchTrigger}
            onClick={search.openSearch}
            aria-label="Search"
          >
            <Search size={16} />
            <span className={styles.searchText}>Search</span>
            <kbd className={styles.searchKbd}>{searchShortcutHint()}</kbd>
          </button>
          {themeToggle}
          {account}
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setOpen((prev) => !prev)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* The bar has no room for the links or CTA at phone widths. */}
      <div className={`${styles.drawer} ${open ? styles.drawerOpen : ''}`}>
        {links}
        {isAuthenticated ? (
          <>
            <Link to="/dashboard" className={styles.cta}>
              Dashboard
            </Link>
            <button type="button" className={styles.drawerLogout} onClick={accountMenu.onLogout}>
              <LogOut size={15} /> Log out
            </button>
          </>
        ) : (
          <>
            <Link to="/login" className={styles.login}>
              Log in
            </Link>
            <Link to="/signup" className={styles.cta}>
              Sign up
            </Link>
          </>
        )}
      </div>

      <AppIndexSearch
        open={search.open}
        onClose={search.closeSearch}
        query={search.query}
        onQueryChange={search.setQuery}
      />
    </>
  )
}
